/**
 * Schaff Trend Cycle Series Component
 *
 * Renders the Schaff Trend Cycle indicator with overbought (75) and oversold (25) zones.
 * Similar to RSISeries but with painted background zones and no middle line.
 *
 * The STC oscillates between 0 and 100, with:
 * - Overbought zone: > 75 (painted background)
 * - Oversold zone: < 25 (painted background)
 * - Neutral zone: 25-75
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import LineSeries from "./LineSeries";
import StaticAreaSeries from "./StaticAreaSeries";
import StraightLine from "./StraightLine";
import SVGComponent from "./SVGComponent";
import { strokeDashTypes } from "../utils";
import {
  extractThresholdValue,
  extractThresholdLine,
  clipToBand,
  clipOutsideBand,
  clipAboveOverBought,
  clipBelowOverSold,
  bandClipRects,
} from "./oscillatorThresholds";

class SchaffTrendCycleSeries extends Component {
  constructor(props) {
    super(props);
    this.renderClip = this.renderClip.bind(this);
    this.topAndBottomClip = this.topAndBottomClip.bind(this);
    this.mainClip = this.mainClip.bind(this);
    this.overBoughtClip = this.overBoughtClip.bind(this);
    this.overSoldClip = this.overSoldClip.bind(this);

    const id1 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId1 = `stc-clip-${id1}`;

    const id2 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId2 = `stc-clip-${id2}`;

    const id3 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId3 = `stc-clip-overbought-${id3}`;

    const id4 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId4 = `stc-clip-oversold-${id4}`;
  }

  topAndBottomClip(ctx, moreProps) {
    const { overSold, overBought } = this.props;
    clipToBand(
      ctx,
      moreProps,
      extractThresholdValue(overBought, 75),
      extractThresholdValue(overSold, 25),
    );
  }

  mainClip(ctx, moreProps) {
    const { overSold, overBought } = this.props;
    clipOutsideBand(
      ctx,
      moreProps,
      extractThresholdValue(overBought, 75),
      extractThresholdValue(overSold, 25),
    );
  }

  overBoughtClip(ctx, moreProps) {
    const { overBought } = this.props;
    clipAboveOverBought(ctx, moreProps, extractThresholdValue(overBought, 75));
  }

  overSoldClip(ctx, moreProps) {
    const { overSold } = this.props;
    clipBelowOverSold(ctx, moreProps, extractThresholdValue(overSold, 25));
  }

  renderClip(moreProps) {
    const { overSold, overBought } = this.props;
    const { overBoughtY, overSoldY, width, height } = bandClipRects(
      moreProps,
      extractThresholdValue(overBought, 75),
      extractThresholdValue(overSold, 25),
    );

    return (
      <defs>
        <clipPath id={this.clipPathId1}>
          <rect
            x={0}
            y={overBoughtY}
            width={width}
            height={overSoldY - overBoughtY}
          />
        </clipPath>
        <clipPath id={this.clipPathId2}>
          <rect x={0} y={0} width={width} height={overBoughtY} />
          <rect x={0} y={overSoldY} width={width} height={height - overSoldY} />
        </clipPath>
        <clipPath id={this.clipPathId3}>
          <rect x={0} y={0} width={width} height={overBoughtY} />
        </clipPath>
        <clipPath id={this.clipPathId4}>
          <rect x={0} y={overSoldY} width={width} height={height - overSoldY} />
        </clipPath>
      </defs>
    );
  }

  render() {
    const { className, stroke, onContextMenu, showZones } = this.props;
    const { yAccessor } = this.props;
    const { overSold, overBought } = this.props;

    const overBoughtData = extractThresholdLine(overBought, 75, {
      color: "#B8C2CC",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });
    const overSoldData = extractThresholdLine(overSold, 25, {
      color: "#B8C2CC",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });

    // Handle both simple stroke object and complex stroke structure
    const stcStroke = stroke.line ? stroke.line : stroke;

    const style1 = { clipPath: `url(#${this.clipPathId1})` };
    const style2 = { clipPath: `url(#${this.clipPathId2})` };
    const style3 = { clipPath: `url(#${this.clipPathId3})` };
    const style4 = { clipPath: `url(#${this.clipPathId4})` };

    // Determine if zones should be shown (default to true)
    const shouldShowZones = showZones !== false;

    // Use overbought/oversold threshold line colors for the fills
    const overBoughtFillColor =
      (overBoughtData &&
        overBoughtData.stroke &&
        overBoughtData.stroke.color) ||
      "#B8C2CC";
    const overSoldFillColor =
      (overSoldData && overSoldData.stroke && overSoldData.stroke.color) ||
      "#B8C2CC";

    return (
      <g className={className}>
        <SVGComponent>{this.renderClip}</SVGComponent>
        {shouldShowZones && (
          <StraightLine
            stroke={overSoldData.stroke}
            yValue={overSoldData.value}
            onContextMenu={onContextMenu}
          />
        )}
        {shouldShowZones && (
          <StraightLine
            stroke={overBoughtData.stroke}
            yValue={overBoughtData.value}
            onContextMenu={onContextMenu}
          />
        )}
        {/* Fill area above overbought line - only shows when indicator is in overbought zone */}
        {shouldShowZones && (
          <StaticAreaSeries
            style={style3}
            canvasClip={this.overBoughtClip}
            className={className}
            yAccessor={yAccessor}
            fill={overBoughtFillColor}
            opacity={0.2}
            base={(yScale) => yScale(overBoughtData.value)}
          />
        )}
        {/* Fill area below oversold line - only shows when indicator is in oversold zone */}
        {shouldShowZones && (
          <StaticAreaSeries
            style={style4}
            canvasClip={this.overSoldClip}
            className={className}
            yAccessor={yAccessor}
            fill={overSoldFillColor}
            opacity={0.2}
            base={(yScale) => yScale(overSoldData.value)}
          />
        )}
        <LineSeries
          style={style1}
          canvasClip={this.topAndBottomClip}
          className={className}
          yAccessor={yAccessor}
          stroke={stcStroke}
          onContextMenu={onContextMenu}
        />
        <LineSeries
          style={style2}
          canvasClip={this.mainClip}
          className={className}
          yAccessor={yAccessor}
          stroke={stcStroke}
          onContextMenu={onContextMenu}
        />
      </g>
    );
  }
}

SchaffTrendCycleSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  stroke: PropTypes.oneOfType([
    // Simple stroke object (from form)
    PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
      width: PropTypes.number,
      dash: PropTypes.oneOf(strokeDashTypes),
    }),
    // Complex stroke structure (backwards compatibility)
    PropTypes.shape({
      line: PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
        width: PropTypes.number,
        dash: PropTypes.oneOf(strokeDashTypes),
      }),
    }),
  ]).isRequired,
  overSold: PropTypes.number.isRequired,
  overBought: PropTypes.number.isRequired,
  showZones: PropTypes.bool,
  onContextMenu: PropTypes.func,
};

SchaffTrendCycleSeries.defaultProps = {
  className: "react-stockcharts-stc-series",
  stroke: { color: "#2196F3", opacity: 1, width: 1, dash: "Solid" },
  overSold: 25,
  overBought: 75,
  showZones: true,
};

export default SchaffTrendCycleSeries;
